import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FiEdit2, FiTrash2, FiSearch, FiPlus, FiLink } from 'react-icons/fi';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import DataTable from '../../components/DataTable/DataTable';
import Modal from '../../components/Modal/Modal';
import Input from '../../components/Input/Input';
import api from '../../api';
import './Blogs.css';

const INITIAL_FORM = {
  title: '',
  icon: '',
  description: '',
  link_url: ''
};

const Blogs = ({ showAlert, showConfirm }) => {
  const [blogs, setBlogs] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/blogs.php');
      setBlogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showAlert("Sync Error", err.response?.data?.error || "Failed to load blog list.", "confirm");
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title || !form.link_url) {
      showAlert("Required fields", "Please fill in the Blog Title and Link URL.", "confirm");
      return;
    }

    try {
      if (editingId) {
        await api.put('/blogs.php', { ...form, id: editingId });
        showAlert("Success", "Blog updated successfully!", "success");
      } else {
        await api.post('/blogs.php', form);
        showAlert("Success", "New blog added successfully!", "success");
      }
      setShowModal(false);
      setEditingId(null);
      setForm(INITIAL_FORM);
      fetchBlogs();
    } catch (err) {
      showAlert("Process failed", err.response?.data?.error || "Action could not be completed.", "confirm");
    }
  };

  const handleDelete = async (id) => {
    showConfirm("Delete Blog", "Are you sure you want to delete this blog? This action is permanent.", async () => {
      try {
        await api.delete(`/blogs.php?id=${id}`);
        fetchBlogs();
        showAlert("Deleted", "Blog has been removed.", "success");
      } catch (err) {
        showAlert("Delete failed", err.response?.data?.error || "Could not delete blog.", "confirm");
      }
    });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filtered = useMemo(() => {
    let data = blogs.filter(b =>
      b.title?.toLowerCase().includes(search.toLowerCase()) ||
      b.description?.toLowerCase().includes(search.toLowerCase())
    );

    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [blogs, search, sortConfig]);

  const columns = [
    { key: 'title', label: 'Blog Details', sortable: true, render: (val, row) => (
      <div className="blog-title-cell">
        <div className="blog-icon-circle">
          <i className={row.icon || 'fa-solid fa-newspaper'} />
        </div>
        <div className="blog-meta-stack">
          <span className="blog-name">{val}</span>
          <span className="blog-date">Added on: {new Date(row.created_at).toLocaleDateString()}</span>
        </div>
      </div>
    )},
    { key: 'description', label: 'Description', render: (val) => (
      <span className="blog-desc-text" title={val}>{val || '—'}</span>
    )},
    { key: 'link_url', label: 'External Link', render: (val) => (
      <a href={val} target="_blank" rel="noopener noreferrer" className="blog-link-btn">
        <FiLink /> Link
      </a>
    )},
    { key: 'actions', label: 'Actions', render: (_, row) => (
      <div className="table-actions">
        <button className="table-action-btn table-action-btn--edit" onClick={() => { setEditingId(row.id); setForm({ ...row }); setShowModal(true); }}><FiEdit2 /></button>
        <button className="table-action-btn table-action-btn--delete" onClick={() => handleDelete(row.id)}><FiTrash2 /></button>
      </div>
    )}
  ];

  return (
    <div className="blogs-page">
      <div className="page-header">
        <div>
          <h2 className="page-header__title">Blog Management</h2>
          <p className="page-header__sub">Publish external blogs, updates and newsletters</p>
        </div>
        <Button variant="primary" icon={<FiPlus />} onClick={() => { setEditingId(null); setForm(INITIAL_FORM); setShowModal(true); }}>Add Blog Post</Button>
      </div>

      <Card padding="none">
        <div className="shared-toolbar">
          <div className="shared-search-box">
            <FiSearch className="shared-search-box__icon" />
            <input
              type="text"
              placeholder="Search by title or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="shared-search-box__input"
            />
          </div>
        </div>
        <DataTable columns={columns} data={filtered} loading={loading} onSort={handleSort} sortConfig={sortConfig} />
      </Card>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingId ? 'Edit Blog Post' : 'Add New Blog Post'}>
        <form onSubmit={handleSubmit} className="professional-form">
          <div className="form-grid form-grid--2">
            <Input id="title" name="title" label="Blog Title" value={form.title} onChange={handleChange} required className="form-grid__span-2" />
            <Input id="icon" name="icon" label="Icon (FontAwesome Class)" value={form.icon} onChange={handleChange} placeholder="e.g. fa-solid fa-graduation-cap" />
            <Input id="link_url" name="link_url" label="Blog Link URL" type="url" value={form.link_url} onChange={handleChange} required prefix={<FiLink />} />
            
            <div className="input-group form-grid__span-2">
              <label htmlFor="description" className="input-label">Description</label>
              <div className="input-wrapper">
                <textarea
                  id="description"
                  name="description"
                  className="input-field"
                  style={{ minHeight: '80px', padding: '10px', resize: 'vertical' }}
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Short description or summary of the blog post..."
                />
              </div>
            </div>
          </div>
          <div className="form-actions-bar">
            <Button type="submit" variant="primary" icon={editingId ? <FiEdit2 /> : <FiPlus />}>{editingId ? 'Save Changes' : 'Add Blog'}</Button>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Blogs;
